import axios from 'axios';
import {Alert, Linking, Platform} from 'react-native';
import {useCallback} from 'react';
import Geolocation from 'react-native-geolocation-service';
import {useMapsContext} from '~providers/maps/zustand';
import PLATFORM from '../../../types/platform';
import {getAddressComponent} from '../LocationModal';
import {BASE_API_URL, GOOGLE_API_KEY} from '@env';
import {useUpdateUser} from '~hooks/user/useUpdateUser';

// Only import native-only modules on native platforms
let PermissionsAndroid: any;
let ToastAndroid: any;
if (Platform.OS !== 'web') {
  const RNModules = require('react-native');
  PermissionsAndroid = RNModules.PermissionsAndroid;
  ToastAndroid = RNModules.ToastAndroid;
}

const useGetCurrentPosition = () => {
  const {setUserLocation, setCurrentPositionState, setPermissionStatus, permissionStatus} = useMapsContext();
  const {mutateAsync: updateUserMutation} = useUpdateUser();

  const getLocationName = async (latitude: number, longitude: number) => {
    const apiKey = GOOGLE_API_KEY;
    const url =`${BASE_API_URL}/google-geocode/reverse?latitude=${latitude}&longitude=${longitude}&language=en`;

    try {
      const response = await axios.get(url);
      const addressComponents = response.data.results[0].address_components;
      console.log(addressComponents, 'addressComponents');
      
      const city = getAddressComponent(addressComponents, 'locality');
      const city2 = getAddressComponent(addressComponents, 'postal_town');
      const state = getAddressComponent(addressComponents, 'administrative_area_level_1');
      const country = getAddressComponent(addressComponents, 'country');
      return `${city || city2 || state}, ${country}`;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const getCurrentPositionGeolocation = (shouldSetCurrentPositionState?: boolean) => {
    // Web platform implementation
    if (Platform.OS === 'web') {
      const nav = navigator as any;
      if (!nav.geolocation) {
        console.warn('Geolocation is not supported by this browser.');
        setUserLocation(null);
        return;
      }

      nav.geolocation.getCurrentPosition(
        async (position: any) => {
          const {latitude, longitude} = position.coords;
          const locationName = await getLocationName(latitude, longitude);
          setUserLocation({
            coords: {
              latitude,
              longitude,
              accuracy: position.coords.accuracy || 0,
              altitude: position.coords.altitude,
              heading: position.coords.heading,
              speed: position.coords.speed,
            },
            timestamp: position.timestamp,
            locationName,
          } as any);

          if (shouldSetCurrentPositionState) {
            setCurrentPositionState({
              latitude,
              longitude,
              address: locationName || '',
              country: locationName?.split(', ').pop() || '',
            });
            await updateUserMutation({
              coords_real: {lat: latitude, long: longitude},
            });
          }
        },
        (error: any) => {
          console.error('Geolocation error:', error);
          setUserLocation(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        },
      );
      return;
    }

    Geolocation.getCurrentPosition(
      async position => {
        const locationName = await getLocationName(position.coords.latitude, position.coords.longitude);
        setUserLocation({...position, locationName});
        if (shouldSetCurrentPositionState) {
          setCurrentPositionState({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            address: locationName || '',
            country: locationName?.split(', ').pop() || '',
          });
          await updateUserMutation({
            coords_real: {lat: position.coords.latitude, long: position.coords.longitude},
          });
        }
      },
      error => {
        if (error.code === 3) {
          return;
        }
        setUserLocation(null);
        return;
      },
      {
        accuracy: {
          android: 'high',
          ios: 'best',
        },
        enableHighAccuracy: true,
        timeout: 15000,
        distanceFilter: 0,
        forceRequestLocation: true,
        forceLocationManager: true,
        showLocationDialog: true,
      },
    );
  };

  const openSetting = () => {
    Linking.openSettings().catch(() => {
      Alert.alert('Unable to open settings');
    });
  };

  const getCurrentPosition = useCallback((shouldSetCurrentPositionState?: boolean, shouldResetIfNoPermissions?: boolean) => {
    // Web platform implementation using browser's Geolocation API
    if (Platform.OS === 'web') {
      const nav = navigator as any;
      if (!nav.geolocation) {
        console.warn('Geolocation is not supported by this browser.');
        setUserLocation(null);
        return;
      }

      nav.geolocation.getCurrentPosition(
        async (position: any) => {
          const {latitude, longitude} = position.coords;
          const locationName = await getLocationName(latitude, longitude);
          setUserLocation({
            coords: {
              latitude,
              longitude,
              accuracy: position.coords.accuracy || 0,
              altitude: position.coords.altitude,
              heading: position.coords.heading,
              speed: position.coords.speed,
            },
            timestamp: position.timestamp,
            locationName,
          } as any);

          if (shouldSetCurrentPositionState) {
            setCurrentPositionState({
              latitude,
              longitude,
              address: locationName || '',
              country: locationName?.split(', ').pop() || '',
            });
            await updateUserMutation({
              coords_real: {lat: latitude, long: longitude},
            });
          }
        },
        (error: any) => {
          console.error('Geolocation error:', error);
          setUserLocation(null);

          if (error.code === 1) {
            // PERMISSION_DENIED
            if (shouldResetIfNoPermissions) {
              updateUserMutation({
                coords_real: null,
              });
            }
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        },
      );
      return;
    }

    const hasPermissionIOS = async () => {
      const status = await Geolocation.requestAuthorization('whenInUse');

      if (status === 'granted') {
        return true;
      }

      if (status === 'disabled') {
        setUserLocation(null);
        Alert.alert(
          'Settings',
          'If you want to use your location to see where you are, you can enable the location services in the settings',
          [
            {text: 'Go to Settings', onPress: openSetting},
            {text: "Don't Use Location", onPress: () => {}},
          ],
        );
      }

      return false;
    };

    const hasLocationPermission = async () => {
      if (Platform.OS === 'ios') {
        const hasPermission = await hasPermissionIOS();
        setPermissionStatus(hasPermission ? 'granted' : 'denied');
        return hasPermission;
      }

      if (Platform.OS === 'android' && Platform.Version < 23) {
        return true;
      }

      const hasPermission = await PermissionsAndroid.check(PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION);

      if (hasPermission) {
        return true;
      }

      const status = await PermissionsAndroid.request(PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION);

      if (status === PermissionsAndroid.RESULTS.GRANTED) {
        setPermissionStatus(status);
        return true;
      }

      if (status === PermissionsAndroid.RESULTS.DENIED) {
        setPermissionStatus(status);
        setUserLocation(null);
        ToastAndroid.show('Location permission denied by user.', ToastAndroid.LONG);
      } else if (status === PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN) {
        setPermissionStatus(status);
        setUserLocation(null);
        ToastAndroid.show('Location permission revoked by user.', ToastAndroid.LONG);
      }

      return false;
    };

    const getLocation = async () => {
      const hasPermission = await hasLocationPermission();

      if (!hasPermission) {
        if (shouldResetIfNoPermissions) {
          await updateUserMutation({
            coords_real: null,
          });
        }
        return;
      }

      getCurrentPositionGeolocation(shouldSetCurrentPositionState);
    };
    getLocation();
  }, [updateUserMutation, setUserLocation, setCurrentPositionState, permissionStatus, setPermissionStatus]);

  const handleUpdatePositionAfterSettings = useCallback(() => {
    getCurrentPositionGeolocation();
  }, []);

  const handleGoToSettings = useCallback(() => {
    if (PLATFORM.isAndroid) {
      if (permissionStatus === PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN) {
        setPermissionStatus(null);
        return Alert.alert(
          'Settings',
          'If you want to use your location to see where you are, you can enable the location services in the settings',
          [
            {
              text: 'Go to Settings',
              onPress: openSetting,
            },
            {
              text: "Don't Use Location",
              onPress: () => {
                setPermissionStatus(PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN);
              },
            },
          ],
        );
      }
      return getCurrentPosition();
    }
    if (PLATFORM.isIos) {
      return Alert.alert(
        'Settings',
        'If you want to use your location to see where you are, you can enable the location services in the settings',
        [
          {
            text: 'Go to Settings',
            onPress: openSetting,
          },
          {text: "Don't Use Location", onPress: () => {}},
        ],
      );
    }
  }, [permissionStatus, setPermissionStatus, getCurrentPosition]);

  return {
    getLocationName,
    getCurrentPosition,
    handleGoToSettings,
    handleUpdatePositionAfterSettings,
    getCurrentPositionGeolocation,
  };
};

export default useGetCurrentPosition;
